import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Informations from "./Profil/Informations";
import Contrats from "./Profil/Contrats";
import Bulletins from "./Profil/Bulletins";
import Documents from "./Profil/Documents";
import { apiService } from "../../components/Config/api.service";
import EmployeeInfo from "./EmployeeInfo";
import Parametre from "./Profil/Parametre";
import { FaArrowLeft, FaArrowRight, FaArchive, FaTrashRestore } from "react-icons/fa";
import FinalSettlement from "./Profil/FinalSettlement";
import LeaveDeparture from "./Profil/LeaveDeparture";
import Drawer from "../../components/Tools/DrawerCustom";
import ContractForm from "./Contract/ContractEmpForm";
import ExternalContractForm from "./Contract/ExternalContractForm";
import EmployeeForm from "./EmployeeForm"; 
import ActionDropdown from "../../components/Utils/ActionDropdown";
import { notify } from "../../components/Utils/Notifications"; 

function Employee() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState({
    name: "",
    email: "",
    poste: "",
    manager: "",
    phone: "",
    address: "",
    maritalStatus: "",
    spouseIncome: false,
    numberOfChildren: 0,
    nationality: "",
    birthday: "",
    image: false,
    active:true,
  });
  const [contracts, setContracts] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [payslipData, setPayslipData] = useState();
  const [primes, setPrimes] = useState([]);
  const [currentContract, setCurrentContract] = useState(null);
  const [externalContract, setExternalContract] = useState([]);
  const [finalSettlementRecap, setFinalSettlementRecap] = useState(null); 
  const [leaveDepartureRecap, setLeaveDepartureRecap] = useState(null);
  const [activeTab, setActiveTab] = useState("Informations");
  const [isDrawerOpenContracts, setIsDrawerOpenContracts] = useState(false);
  const [isDrawerOpenExternalContracts, setIsDrawerOpenExternalContracts] = useState(false);
  const [isDrawerOpenInformations, setIsDrawerOpenInformations] = useState(false);
  const [prevId, setPrevId] = useState(null);
  const [nextId, setNextId] = useState(null);
  const [prevName, setPrevName] = useState("");
  const [nextName, setNextName] = useState("");

  const handleBack = useCallback(() => {
    if (location.search) navigate(`/employees${location.search}`);
    else navigate('/employees');
  }, [location, navigate]);

  const refreshEmployeeinfo = useCallback(() => {
    apiService.getElementById("/employee", id)
      .then((response) => setEmployee(response.data))
      .catch((error) => console.error("Error fetching employee:", error));
  }, [id]);

  const refreshContractInfo = useCallback(() => {
    apiService.getElementById("/employee/contracts", id)
      .then((response) => {
        const openContract = response.data.contracts.find(contract => contract.Statut === "open") || null;
        setCurrentContract(openContract);
        setContracts(response.data.contracts);
        if (openContract == null) {
            apiService.getElementById("/external_contract/employee", id)
            .then((response) => {
              setExternalContract(response.data.external_contract);
            }).catch(() => setExternalContract(null))
        } else {
            // clear externalContract when a regular contract is present
            setExternalContract(null);
        }
      })
      .catch((error) => console.error("Error fetching contracts:", error));
  }, [id]);

  const refreshFinalSettlementRecap = useCallback(() => {
    if (id && currentContract?.id) {
      apiService.getElementById("/final_settlement/recap", currentContract.id)
        .then(res => setFinalSettlementRecap(res.data.final_settlement))
        .catch(() => setFinalSettlementRecap(null));
    }
  }, [id, currentContract]);

  const refreshLeaveDepartureRecap = useCallback(() => {
    if (id && currentContract?.id) {
      apiService.getElementById("/leave_departure/recap", currentContract.id)
        .then(res => setLeaveDepartureRecap(res.data.leaves_departure))
        .catch(() => setLeaveDepartureRecap(null));
    }
  }, [id, currentContract]);

  

  const refreshPrimesInfo = useCallback(() => {
    apiService.getElementById("/employee/primes", id)
      .then((response) => setPrimes(response.data.primes))
      .catch((error) => console.error("Error fetching primes:", error));
  }, [id]);

  const refreshPayslipInfo = useCallback(() => {
    apiService.getElementById("/employee/payslips", id)
      .then((response) => {
        const fetchedPayslips = response.data.payslips;
        setPayslips(fetchedPayslips);
        const currentPayslip = fetchedPayslips.find((p) =>
          p.De.startsWith(new Date().toISOString().slice(0, 7))
        );
        setPayslipData(currentPayslip);
      })
      .catch((error) => console.error("Error fetching payslips:", error));
  }, [id]);


  // Rafraîchir les infos liées au contrat courant
  useEffect(() => {
    if (currentContract?.id) {
      refreshFinalSettlementRecap();
      refreshLeaveDepartureRecap();
    }
  }, [currentContract, refreshFinalSettlementRecap, refreshLeaveDepartureRecap]);

  // Rafraîchir toutes les infos quand l'id change
  useEffect(() => {
    if (id) {
      refreshEmployeeinfo();
      refreshContractInfo();
      refreshPrimesInfo();
      refreshPayslipInfo();
    }
  }, [id, refreshEmployeeinfo, refreshContractInfo, refreshPrimesInfo, refreshPayslipInfo]);

  // Récupère les IDs précédent et suivant à chaque changement d'id
  useEffect(() => {
    if (id) {
      apiService.getElement(`/employee/navigation/${id}`)
        .then(res => {
          setPrevId(res.data.prev_id);
          setNextId(res.data.next_id);
          setPrevName(res.data.prev_name || "");
          setNextName(res.data.next_name || "");
        });
    }
  }, [id]);

  const handlePrev = () => {
    if (prevId) navigate(`/employees/${prevId}`);
  };
  const handleNext = () => {
    if (nextId) navigate(`/employees/${nextId}`);
  };

  const handleArchive = useCallback(async (archiveStatus) => {
      try {
          await apiService.saveElement('/employees/archive', {
              // tranforme l'id en integer
              employee_ids: [ parseInt(id)],
              archive: archiveStatus,
          });
          const action = archiveStatus ? "archivé" : "désarchivé";
          notify(`Employé ${action} avec succès`, "", 2);
          refreshEmployeeinfo();
      } catch (e) {
          const action = archiveStatus ? "l'archivage" : "la désarchivage";
          notify(`Erreur lors de ${action}`, "", 3);
          console.error(`Erreur lors de ${action}`, e);
      }
  }, [id, refreshEmployeeinfo]);

  const handleFormSuccessEmployee = useCallback(() => {
    setIsDrawerOpenInformations(false);
    refreshEmployeeinfo();
  }, [refreshEmployeeinfo]);

  const handleFormSuccessContract = useCallback(() => {
    setIsDrawerOpenContracts(false);
    refreshContractInfo();
    refreshPayslipInfo();
    refreshPrimesInfo();
  }, [refreshContractInfo, refreshPayslipInfo, refreshPrimesInfo]);

  const handleFormSuccessExternalContract = useCallback(() => {
    setIsDrawerOpenExternalContracts(false);
    // refresh contracts/external contract after create/update
    refreshContractInfo();
    refreshPayslipInfo();
    refreshPrimesInfo();
  }, [refreshContractInfo, refreshPayslipInfo, refreshPrimesInfo]);

  const handleFinalSettlement = useCallback(() => {
    refreshFinalSettlementRecap();
    setActiveTab("Contrats");
  }, [refreshFinalSettlementRecap]);

  const handleLeaveDeparture = useCallback(() => {
    refreshLeaveDepartureRecap(); 
    setActiveTab("Contrats");
  }, [refreshLeaveDepartureRecap]);

  const handleDeleteFinalSettlement = useCallback(async () => {
    if (!finalSettlementRecap?.id) return;
    try {
      await apiService.updateElementById("/final_settlement/delete", finalSettlementRecap.id, []);
      handleFinalSettlement();
    } catch (err) {
      console.error("Erreur lors de la suppression du final settlement", err);
    }
  }, [finalSettlementRecap, handleFinalSettlement]);

  const handleDeleteLeaveDeparture = useCallback(async () => {
    if (!leaveDepartureRecap?.id) return;
    try {
      await apiService.updateElementById("/leave_departure/delete", leaveDepartureRecap.id, []);
      refreshLeaveDepartureRecap();
    } catch (err) {
      console.error("Erreur lors de la suppression du départ en congé", err);
    }
  }, [leaveDepartureRecap, refreshLeaveDepartureRecap]);

  const renderContent = useMemo(() => {
    switch (activeTab) {
      case "Informations":
        return <Informations 
                setIsDrawerOpen={setIsDrawerOpenInformations} 
                employee={employee} 
                payslipData={payslipData} 
                currentContract={currentContract}
              />;
      case "Contrats":
        return <Contrats 
                finalSettlementRecap={finalSettlementRecap} 
                leaveDepartureRecap={leaveDepartureRecap}
                setIsDrawerOpenInformations={setIsDrawerOpenInformations} 
                setIsDrawerOpenContracts={setIsDrawerOpenContracts} 
                setIsDrawerOpenExternalContracts={setIsDrawerOpenExternalContracts}
                primes={primes} 
                contracts={contracts} 
                setActiveTab={setActiveTab} 
                employee={employee} 
                currentContract={currentContract} 
                externalContract={externalContract} 
                id={id} 
                refreshContractInfo={refreshContractInfo}    // <--- added
                handleDeleteFinalSettlement={handleDeleteFinalSettlement}    
                handleDeleteLeaveDeparture={handleDeleteLeaveDeparture}            
              />;
      case "Bulletins":
        return <Bulletins payslips={payslips} />;
      case "Documents":
        return <Documents />;
      case "Parametres":
        return <Parametre />;
      case "FinalSettlement":
        return <FinalSettlement 
                setActiveTab={setActiveTab} 
                employee={employee} 
                currentContract={currentContract}
                finalSettlementId={finalSettlementRecap?.id} 
                refreshRecap={handleFinalSettlement}
                handleDeleteFinalSettlement={handleDeleteFinalSettlement}
              />;
      case "LeaveDeparture":
        return (
          <LeaveDeparture
            setActiveTab={setActiveTab}
            employee={employee}
            currentContract={currentContract}
            leaveDepartureId={leaveDepartureRecap?.id}
            refreshRecap={handleLeaveDeparture}
            handleDeleteLeaveDeparture={handleDeleteLeaveDeparture}
          />
        );
      default:
        return <Informations employee={employee} />;
    }
  }, [
    activeTab, employee, payslipData, currentContract, finalSettlementRecap, leaveDepartureRecap,
    primes, contracts, id, handleDeleteFinalSettlement, handleDeleteLeaveDeparture,
    handleFinalSettlement, handleLeaveDeparture, payslips, externalContract
  ]);

  // const dropdownActions = useMemo(() => [
  //   {
  //       label: employee.active ? "Archiver" : "Désarchiver",
  //       icon: employee.active ? <FaArchive /> : <FaTrashRestore />,
  //       onClick: () => handleArchive(employee.active),
  //       color: employee.active ? 'text-red-500' : 'text-green-500'
  //   }
  // ], [employee.active, handleArchive]);

  return (
    <>
      <div className="flex items-center gap-4 justify-between mb-4">
        <div className="flex justify-between  items-center gap-2">
          <button onClick={handleBack} className="flex items-center gap-4 text-[--color-primary]">
            <FaArrowLeft className="text-[--color-primary]" />
            Retourner à la liste des collaborateurs
          </button>          
        </div>
        {employee.active ?(
        <div className="flex items-center gap-2">
          <button
              onClick={handlePrev}
              disabled={!prevId}
              className={`ml-2 px-2 py-1 rounded-full flex items-center gap-2 ${!prevId ? 'bg-gray-200 text-gray-400' : 'bg-[--color-primary] text-white'}`}
              title={prevName ? `Précédent : ${prevName}` : "Précédent"}
            >
              <FaArrowLeft />
              {prevName && <span className="hidden md:inline">{prevName}</span>}
            </button>
            <button
              onClick={handleNext}
              disabled={!nextId}
              className={`ml-2 px-2 py-1 rounded-full flex items-center gap-2 ${!nextId ? 'bg-gray-200 text-gray-400' : 'bg-[--color-primary] text-white'}`}
              title={nextName ? `Suivant : ${nextName}` : "Suivant"}
            >
              {nextName && <span className="hidden md:inline">{nextName}</span>}
              <FaArrowRight />
            </button>
        </div>
        ) : (
        <div className="flex items-center gap-2"> 
                <span className="badge badge-warning gap-2">
                    <FaArchive />
                    Archivé
                </span> 
            {/* <ActionDropdown actions={dropdownActions} /> */}
        </div>
        )}
      </div>
      <div className="flex md:flex-row rounded-lg gap-4 w-full">
        <div className="rounded-lg bg-gray-50 w-72">
          <EmployeeInfo
            employee={employee}
            currentContract={currentContract}
            setActiveTab={setActiveTab}
            handleArchive={handleArchive}
          />
        </div>

        <div className=" rounded-lg overflow-hidden col-span-4  bg-white flex-1">
          {activeTab !== "FinalSettlement" && (
            <div className="p-3 bg-white flex justify-start gap-10 md:flex-wrap mb-3">
              <button
                className={`pb-2 ${activeTab === "Informations" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Informations")}
              >
                General
              </button>
              <button
                className={`pb-2 ${activeTab === "Contrats" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Contrats")}
              >
                Contrat
              </button>
              <button
                className={`pb-2 ${activeTab === "Documents" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Documents")}
              >
                Documents
              </button>
              {/* <button
                className={`pb-2 ${activeTab === "Parametres" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Parametres")}
              >
                Parametre du compte
              </button> */}
            </div>
          )}
          {/* Drawer pour le formulaire de contrat */}
          <Drawer isOpen={isDrawerOpenContracts} setIsOpen={setIsDrawerOpenContracts}>
            <ContractForm
              currentContract={currentContract}
              employee={employee}
              refreshContract={refreshContractInfo}
              onSuccess={handleFormSuccessContract}
            />
          </Drawer>
          {/* Drawer pour le formulaire de contrat externe */}
          <Drawer isOpen={isDrawerOpenExternalContracts} setIsOpen={setIsDrawerOpenExternalContracts}>
            <ExternalContractForm
              employee={employee}
              currentExternal={externalContract}
              onSuccess={handleFormSuccessExternalContract}
              onCancel={() => setIsDrawerOpenExternalContracts(false)}
            />
          </Drawer>
          {/* Drawer pour modifier les informations */}
          <Drawer isOpen={isDrawerOpenInformations} setIsOpen={setIsDrawerOpenInformations}>
            <EmployeeForm
              refreshEmployee={refreshEmployeeinfo}
              onSuccess={handleFormSuccessEmployee}
            />
          </Drawer>
          {renderContent}
        </div>
      </div>
    </>
  );
}

export default Employee;